import { userDtos } from '@vectronic/openApi-customer';
import { Moment } from 'moment';

export class DateHelper {
  static isDateInDeployments(date: string, deployments: userDtos.Deployment[]) {
    for (const { startDate, endDate } of deployments) {
      if (date >= startDate && (!endDate || date <= endDate)) return true;
    }
    return false;
  }
  private static isDateOutsideOfDeployments(date: string, deployments: userDtos.Deployment[]) {
    return !this.isDateInDeployments(date, deployments);
  }

  static deploymentsLastStartDate(deployments: userDtos.Deployment[]) {
    let lastStartDate: string | undefined;
    for (const { startDate } of deployments) {
      if (!lastStartDate || startDate > lastStartDate) lastStartDate = startDate;
    }
    return lastStartDate;
  }

  static deploymentsFirstStartDate(deployments: userDtos.Deployment[]) {
    let firstStartDate: string | undefined;
    for (const { startDate } of deployments) {
      if (!firstStartDate || startDate < firstStartDate) firstStartDate = startDate;
    }
    return firstStartDate;
  }

  private static hasNoOverlap(
    deployment: userDtos.Deployment,
    otherDeployments: userDtos.Deployment[],
  ) {
    return !this.hasOverlap(deployment, otherDeployments);
  }
  static hasOverlap(
    { startDate, endDate }: userDtos.Deployment,
    otherDeployments: userDtos.Deployment[],
  ) {
    for (const other of otherDeployments) {
      const startsBeforeOtherEnds = !other.endDate || startDate <= other.endDate;
      const endsAfterOtherStarts = !endDate || endDate >= other.startDate;
      if (startsBeforeOtherEnds && endsAfterOtherStarts) return true;
    }
    return false;
  }

  static isValidEndDate(
    endDate: string | undefined,
    startDate: string | undefined,
    deployments: userDtos.Deployment[],
  ) {
    if (!startDate) return this.isDateOutsideOfDeployments(endDate, deployments);
    return startDate < endDate && this.hasNoOverlap({ startDate, endDate }, deployments);
  }

  static isValidStartDate(
    startDate: string,
    endDate: string | undefined,
    deployments: userDtos.Deployment[],
  ) {
    if (!startDate) return false;
    if (!endDate) return this.isDateOutsideOfDeployments(startDate, deployments);
    return startDate < endDate && this.hasNoOverlap({ startDate, endDate }, deployments);
  }

  private static datePickerDateToIso(date: Date) {
    const offsetInMillis = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - offsetInMillis).toISOString();
  }

  static datePickerMomentToIsoDayStart(moment?: Moment) {
    return this.datePickerDateToIsoDayStart(moment?.toDate());
  }
  static datePickerDateToIsoDayStart(date?: Date) {
    if (!date) return;
    const utcIso = this.datePickerDateToIso(date);
    return this.toDayStart(utcIso);
  }

  static datePickerMomentToIsoDayEnd(moment?: Moment) {
    return this.datePickerDateToIsoDayEnd(moment?.toDate());
  }
  static datePickerDateToIsoDayEnd(date?: Date) {
    if (!date) return;
    const utcIso = this.datePickerDateToIso(date);
    return this.toDayEnd(utcIso);
  }

  static isoToDatePickerDate(iso?: string) {
    if (!iso) return null;
    const date = new Date(iso);
    const offsetInMillis = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + offsetInMillis);
  }

  static isoToTimezoneDatePickerDate(iso?: string, timeZone = 'UTC') {
    if (!iso) return null;
    const date = this.isoToDatePickerDate(iso);
    return this.toTimezoneDate(date, timeZone);
  }

  static timezoneDatePickerMomentToIsoDayStart(moment?: Moment, timeZone?: string) {
    return this.timezoneDatePickerDateToIsoDayStart(moment?.toDate(), timeZone);
  }
  static timezoneDatePickerDateToIsoDayStart(date?: Date, timeZone = 'UTC') {
    if (!date) return;
    let timezoneIso = this.datePickerDateToIso(date);
    timezoneIso = this.toDayStart(timezoneIso);
    return this.fromTimezone(timezoneIso, timeZone);
  }

  static timezoneDatePickerMomentToIsoDayEnd(moment?: Moment, timeZone?: string) {
    return this.timezoneDatePickerDateToIsoDayEnd(moment?.toDate(), timeZone);
  }
  static timezoneDatePickerDateToIsoDayEnd(date?: Date, timeZone = 'UTC') {
    if (!date) return;
    let timezoneIso = this.datePickerDateToIso(date);
    timezoneIso = this.toDayEnd(timezoneIso);
    return this.fromTimezone(timezoneIso, timeZone);
  }

  static toTimezone(dateString: string, timeZone: string) {
    const date = new Date(dateString);
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return date.toLocaleString('sv-SE', { timeZone }).replace(' ', 'T') + '.' + ms + 'Z';
  }
  private static toTimezoneDate(date: Date, timeZone: string) {
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return new Date(date.toLocaleString('sv-SE', { timeZone }).replace(' ', 'T') + '.' + ms + 'Z');
  }

  static fromTimezone(timezoneIso: string, timeZone = 'UTC') {
    const date = new Date(timezoneIso);
    const double = this.toTimezoneDate(date, timeZone);
    const offset = double.getTime() - date.getTime();
    return new Date(date.getTime() - offset).toISOString();
  }

  private static toDayStart(date: string) {
    return `${date.split('T')[0]}T00:00:00.000Z`;
  }

  private static toDayEnd(date?: string) {
    if (!date) return;
    return `${date.split('T')[0]}T23:59:59.999Z`;
  }
}
